import { FC } from "react";
import { useIntl } from "react-intl";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";

/**
 * Maps label type identifiers to their i18n message IDs for tooltip descriptions.
 */
const LABEL_DESCRIPTIONS: Record<string, string> = {
  documented: "sourceLabel.documented.description",
  "patient-reported": "sourceLabel.patientReported.description",
  "clinical-assessment": "sourceLabel.clinicalAssessment.description",
};

type SourceAttributionLabelProps = React.ComponentPropsWithoutRef<"abbr"> & {
  "data-label-type"?: string;
};

/**
 * Renders a source attribution label as a styled inline element with a tooltip
 * showing the label's description on hover.
 *
 * Used as a react-markdown component override for `<abbr>` elements that have
 * been transformed by the rehypeSourceLabels plugin.
 */
export const SourceAttributionLabel: FC<SourceAttributionLabelProps> = ({
  "data-label-type": labelType,
  node,
  children,
  className,
  ...rest
}: SourceAttributionLabelProps & { node?: unknown }) => {
  const { formatMessage } = useIntl();

  if (!labelType || !(labelType in LABEL_DESCRIPTIONS)) {
    return (
      <abbr className={className} data-label-type={labelType} {...rest}>
        {children}
      </abbr>
    );
  }

  const descriptionId = LABEL_DESCRIPTIONS[labelType];
  const description = formatMessage({ id: descriptionId });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          {...rest}
          className="cursor-pointer underline decoration-dotted text-muted-foreground"
          role="term"
          aria-label={description}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{description}</p>
      </TooltipContent>
    </Tooltip>
  );
};
